import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoImg from '../../assets/wellness-logo.png';
import useResponsive from '../../hooks/useResponsive';

const navItems = [
  { label: 'Home', href: '#home' },
  { label: 'Features', href: '#features' },
  { label: 'Benefits', href: '#benefits' },
  { label: 'Stories', href: '#stories' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isMobileOrTablet } = useResponsive();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          // Premium glassmorphism left-to-right light blue to white gradient
          background: scrolled
            ? 'linear-gradient(90deg, rgba(224, 242, 254, 0.25) 0%, rgba(255, 255, 255, 0.18) 100%)'
            : 'linear-gradient(90deg, rgba(224, 242, 254, 0.12) 0%, rgba(255, 255, 255, 0.08) 100%)',
          backdropFilter: 'blur(20px) saturate(190%)', // High-fidelity frosted glass blur
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          borderBottom: scrolled 
            ? '1px solid rgba(255, 255, 255, 0.45)' 
            : '1px solid rgba(255, 255, 255, 0.25)',
          boxShadow: scrolled 
            ? '0 8px 32px 0 rgba(0, 0, 0, 0.04), inset 0 1px 2px 0 rgba(255, 255, 255, 0.6)' 
            : 'none',
          transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
          py: 0.5,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: '65px !important' }}>
            {/* Logo + Zeniv Brand name */}
            <Box
              component="a"
              href="#home"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                pl:{ xs: '8px', sm: '40px', md: '40px' },
                gap: "12px",
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.015)',
                },
              }}
            >
              <Box
                component="img"
                src={LogoImg}
                alt="Zeniv Logo"
                sx={{
                  height: isMobileOrTablet ? '40px' : '50px',
                  objectFit: 'contain',
                }}
              />
              
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Outfit',
                  fontWeight: 600,
                  fontSize: isMobileOrTablet ? '24px' : '32px',
                  color: '#000000ff',
                  letterSpacing: '-0.3px',
                }}
              >
                Zeniv
              </Typography>
            </Box>

            {/* Desktop Navigation Links */}
            {!isMobileOrTablet && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 4.5,
                  alignItems: 'center',
                  // flex: 1,
                  // justifyContent: 'center',
                  ml: 'auto',
                  mr: 4,
                }}
              >
                {navItems.map((item) => (
                  <Box
                    key={item.label}
                    component="a"
                    href={item.href}
                    sx={{
                      color: '#6B7C72',
                      fontFamily: 'poppins,400',
                      fontWeight: 400,
                      textDecoration: 'none',
                      fontSize: '20px',
                      position: 'relative',
                      transition: 'color 0.3s ease',
                      padding: '4px 0',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '0%',
                        height: '2px',
                        backgroundColor: 'primary.main',
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      },
                      '&:hover': {
                        color: 'primary.main',
                        '&::after': {
                          width: '100%',
                        },
                      },
                    }}
                  >
                    {item.label}
                  </Box>
                ))}
              </Box>
            )}

            {/* Desktop CTA Button */}
            {!isMobileOrTablet && (
              <Box>
                <Button
                  variant="contained"
                  href="#download"
                  
                  sx={{
                    backgroundColor: '#325D44', // Forest Green
                    color: '#FFFFFF',
                    borderRadius: '22px', // Full rounded pill capsule
                    fontWeight: 500,
                    fontSize: '16px',
                    px: 3.5,
                    py: 0.8,
                    mr:'80px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    border: '1px solid rgba(44, 94, 67, 0.1)',
                    '&:hover': {
                      backgroundColor: '#1B3B2B',
                      boxShadow: '0 4px 14px rgba(44, 94, 67, 0.25)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Get the app
                </Button>
              </Box>
            )}

            {/* Mobile Hamburger Button */}
            {isMobileOrTablet && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: 'primary.main',
                  backgroundColor: 'rgba(44, 94, 67, 0.06)',
                  '&:hover': {
                    backgroundColor: 'rgba(44, 94, 67, 0.12)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: '280px', sm: '320px' },
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAF9 100%)',
            boxShadow: '-12px 0 40px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header: Logo + Close */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid rgba(44, 94, 67, 0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src={LogoImg}
              alt="Zeniv"
              sx={{ height: 40, objectFit: 'contain' }}
            />
            <Typography
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 700,
                fontSize: '1.25rem',
                color: '#1a1a1a',
                letterSpacing: '-0.02em',
              }}
            >
              Zeniv
            </Typography>
          </Box>
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{
              color: '#6B7C72',
              backgroundColor: 'rgba(44, 94, 67, 0.05)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(44, 94, 67, 0.12)',
                color: '#2C5E43',
                transform: 'rotate(90deg)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Nav Links */}
        <List sx={{ flex: 1, px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {navItems.map((item, index) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                component="a"
                href={item.href}
                onClick={handleDrawerToggle}
                sx={{
                  borderRadius: '14px',
                  py: 1.0,
                  px: 2,
                  color: '#4a4a4a',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.25s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '0%',
                    backgroundColor: '#2C5E43',
                    borderRadius: '0 4px 4px 0',
                    transition: 'height 0.3s ease',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(44, 94, 67, 0.06)',
                    color: '#2C5E43',
                    pl: 2.5,
                    '&::before': {
                      height: '60%',
                    },
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontFamily: 'Outfit',
                    fontWeight: 500,
                    fontSize: '1.05rem',
                    letterSpacing: '-0.01em',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Footer CTA */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid rgba(44, 94, 67, 0.08)',
            background: 'rgba(44, 94, 67, 0.02)',
          }}
        >
          <Button
            variant="contained"
            fullWidth
            href="#download"
            onClick={handleDrawerToggle}
            sx={{
              backgroundColor: '#2C5E43',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '0.95rem',
              py: 1.2,
              borderRadius: '14px',
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(44, 94, 67, 0.25)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#1B3B2B',
                boxShadow: '0 6px 20px rgba(44, 94, 67, 0.35)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Get the app
          </Button>
          <Typography
            sx={{
              textAlign: 'center',
              mt: 1.5,
              fontSize: '0.75rem',
              color: '#8a9a8e',
              fontFamily: 'Outfit',
            }}
          >
            Start your wellness journey today
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}
